import React, { useState } from 'react';
import { 
  Box, Card, CardContent, CardMedia, Typography, Grid, 
  Button, Chip, Stack, Divider, Container, alpha 
} from '@mui/material';
import { BiRightArrowAlt, BiBuildingHouse, BiCalendarAlt } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { backendURL } from 'redux/reducers/settings';
import {
  useApplyForTrainingMutation,
  useCancelForTrainingMutation,
  useGetTrainingRoutineApplicationQuery
} from 'redux/reducers/user/dashboard';
import ConfirmationModal from 'components/ui/ConfirmationModal/confirmation-modal';
import NascLogo from '../../../../assets/images/logo.png';

const RunningCard = () => {
  const navigate = useNavigate();
  const { data: trainingList = [] } = useGetTrainingRoutineApplicationQuery('');
  const [showApplyModal, setshowApplyModal] = useState(false);
  const [selectedData, setSelectedData] = useState<any>(null);

  const [applyForTraining, { isLoading }] = useApplyForTrainingMutation();

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Approved': return { color: 'success', label: 'Approved' };
      case 'Pending': return { color: 'warning', label: 'Pending' };
      case 'Rejected': return { color: 'error', label: 'Rejected' };
      default: return { color: 'default', label: status };
    }
  };

  const onApplyConfirm = () => {
    if (!selectedData) return;
    applyForTraining({ routine_id: selectedData?.routine_master_id })
      .unwrap()
      .then(() => {
        setshowApplyModal(false);
        toast.success('Applied Successfully');
      })
      .catch(() => toast.error('Application failed'));
  };

  const handleApplyClick = (item: any) => {
    if (item?.application_status === 'Approved') return toast.info('Already Approved');
    setSelectedData(item);
    setshowApplyModal(true);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: 0 }}>
      <ConfirmationModal 
        isOpen={showApplyModal} 
        onClose={() => setshowApplyModal(false)} 
        btnText="Apply Now" 
        onConfirm={onApplyConfirm} 
        title="Confirm Application" 
        isLoading={isLoading}
      >
        <Box sx={{ p: 2 }}>
          <Typography>Are you sure you want to apply for <b>{selectedData?.training_details?.training_name}</b>?</Typography>
        </Box>
      </ConfirmationModal>

      {!trainingList?.length ? (
        <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'background.paper', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Typography color="text.secondary">No active training applications found.</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {trainingList.map((item: any) => {
            const status = getStatusConfig(item.application_status);
            return (
              <Grid item xs={12} sm={6} lg={4} key={item.id}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%', 
                    borderRadius: 4, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(0,0,0,0.1)' }
                  }}
                >
                  <Box sx={{ position: 'relative', bgcolor: '#f8f9fa', p: 3, textAlign: 'center' }}>
                    <CardMedia
                      component="img"
                      image={item.training_details?.image ? `${backendURL}/${item?.training_details?.image}` : NascLogo}
                      sx={{ height: 120, objectFit: 'contain', mx: 'auto' }}
                    />
                    <Chip 
                      label={status.label} 
                      color={status.color as any}
                      size="small"
                      sx={{ 
                        position: 'absolute', top: 16, right: 16, 
                        fontWeight: 700, borderRadius: 1.5,
                        textTransform: 'uppercase', fontSize: '0.65rem'
                      }} 
                    />
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, minHeight: 60, lineHeight: 1.3 }}>
                      {item?.training_details?.training_name}
                    </Typography>

                    <Stack spacing={1.5} sx={{ mb: 3, p: 2, bgcolor: alpha('#f1f3f5', 0.5), borderRadius: 3 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BiCalendarAlt color="#6c757d" />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>START:</Typography>
                        <Typography variant="caption" fontWeight={700}>{item?.start_date}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BiBuildingHouse color="#6c757d" />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>HALL:</Typography>
                        <Typography variant="caption" fontWeight={700} noWrap>{item?.hall_details?.hall_name || 'N/A'}</Typography>
                      </Stack>
                    </Stack>

                    <Button 
                      fullWidth 
                      variant={item.application_status === 'Approved' ? "contained" : "outlined"}
                      color={item.application_status === 'Approved' ? "success" : "primary"}
                      disabled={item.application_status === 'Approved'}
                      onClick={() => handleApplyClick(item)}
                      sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}
                    >
                      {item.application_status === 'Approved' ? 'Applied' : 'Apply Now'}
                    </Button>
                  </CardContent>

                  <Divider />
                  
                  <Box 
                    onClick={() => navigate(`/training/${item.routine_master_id}`)}
                    sx={{ 
                      py: 2, textAlign: 'center', cursor: 'pointer', color: 'primary.main',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Learning Portal</Typography>
                    <BiRightArrowAlt size={20} style={{ marginLeft: 4 }} />
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default RunningCard;